import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Team() {
  const { t } = useLang();
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [me, setMe] = useState(null);
  const [myRole, setMyRole] = useState(null);

  const load = async () => {
    const [team, meRes, org] = await Promise.all([
      api.get("/team"),
      api.get("/auth/me"),
      api.get("/organization"),
    ]);
    setMembers(team.data);
    setMe(meRes.data);
    setMyRole(org.data?.role || "editor");
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const { data } = await api.post("/team/invite", { email, role });
      toast.success(data.status === "added" ? t("memberAdded") : t("memberInvited"));
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const changeRole = async (uid, newRole) => {
    await api.patch(`/team/${uid}`, { role: newRole });
    load();
  };

  const remove = async (uid, email) => {
    if (uid) await api.delete(`/team/${uid}`);
    else await api.delete(`/team/invite/${encodeURIComponent(email)}`);
    toast.success(t("memberRemoved"));
    load();
  };

  const isOwner = myRole === "owner";

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Multi-user</div>
        <h1 className="font-display text-3xl font-semibold mt-1">{t("team")}</h1>
      </div>

      {isOwner && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">{t("inviteMember")}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={invite} className="flex flex-wrap gap-3 items-end" data-testid="invite-form">
              <div className="flex-1 min-w-[220px] space-y-2">
                <Label>{t("email")}</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" data-testid="invite-email" />
              </div>
              <div className="space-y-2">
                <Label>{t("role")}</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="w-40" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">{t("owner")}</SelectItem>
                    <SelectItem value="editor">{t("editor")}</SelectItem>
                    <SelectItem value="viewer">{t("viewer")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="invite-submit">
                <UserPlus size={16} className="mr-2" /> {t("invite")}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="font-display text-lg">{members.length} {members.length === 1 ? "member" : "members"}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2">{t("name")}</th>
                  <th className="text-left px-4 py-2">{t("email")}</th>
                  <th className="text-left px-4 py-2">{t("role")}</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, i) => (
                  <tr key={m.user_id || m.email} className="border-b border-border/60" data-testid={`member-row-${i}`}>
                    <td className="px-4 py-2">{m.name || "—"}</td>
                    <td className="px-4 py-2 font-mono-data text-xs">{m.email}{m.user_id === me?.id && <span className="ml-2 text-xs text-primary">(you)</span>}</td>
                    <td className="px-4 py-2">
                      {isOwner && m.status === "active" && m.user_id !== me?.id ? (
                        <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                          <SelectTrigger className="w-32 h-8" data-testid={`role-select-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">{t("owner")}</SelectItem>
                            <SelectItem value="editor">{t("editor")}</SelectItem>
                            <SelectItem value="viewer">{t("viewer")}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="capitalize">{t(m.role)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] font-mono-data uppercase px-2 py-0.5 rounded-sm ${m.status === "active" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                        {m.status === "active" ? t("active") : t("pending")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {isOwner && m.user_id !== me?.id && (
                        <button onClick={() => remove(m.user_id, m.email)} className="text-muted-foreground hover:text-destructive" data-testid={`member-remove-${i}`}>
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
